import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { isAppInIFrame } from '@thoughtspot/embed-util';
import {
    FEATURE_FLAG_NAMES,
    isFeatureEnabled,
    OBSERVABILITY_CONFIG,
} from '@thoughtspot/feature-service';
import { getClientLocale } from '@thoughtspot/i18n';
import {
    getLogger,
    getMeter,
    getTracer,
    initTelemetry,
    Logger,
    Meter,
    ValueType,
} from '@thoughtspot/observability';
import { isNotAProductionCluster } from '@thoughtspot/session-service';
import { useEffect, useRef } from 'react';
import {
    getClusterId,
    getClusterName,
    getCurrentOrgId,
    getReleaseVersion,
    getSentryAnonymizedDSN,
    getSentryFullDSN,
    getUserGuid,
} from '../config-service/session-service';
import { OTLP_CONFIG } from './observability-service.const';

let LOGGER: Logger = (console as any) as Logger;
let intercomMutationObserver: MutationObserver;

const startFullReplay = async (onReplayStart: (replayId: string) => void, isUnmaskedReplay: boolean) => {
    const { initReplayService, startFullReplay } = await import(
        '@thoughtspot/observability'
    );
    const fullDSN = getSentryFullDSN();
    if (!fullDSN) {
        LOGGER.error(
            'Sentry DSN is empty; skipping full replay initialization',
        );
        return;
    }
    await initReplayService({
        dsn: fullDSN,
        userId: getUserGuid(),
        releaseVersion: getReleaseVersion(),
        clusterId: getClusterId(),
        clusterName: getClusterName(),
        userLocale: getClientLocale(),
        orgId: getCurrentOrgId().toString(),
        isEmbedded: isAppInIFrame(),
        env: isNotAProductionCluster() ? 'dev' : 'production',
    });
    const replayID = await startFullReplay(isUnmaskedReplay);
    onReplayStart(replayID);
};

const isUnmaskedReplay = (): boolean => {
    const observabilityConfig: OBSERVABILITY_CONFIG | any = isFeatureEnabled(
        FEATURE_FLAG_NAMES.OBSERVABILITY_CONFIG,
    );
    return observabilityConfig.replayUnmaskedTenants.indexOf(getCurrentOrgId().toString()) !== -1;
};

const watchMessengerFrame = (onReplayStart: (replayId: string) => void) => {
    const intercomMessengerFrame = document.querySelector(
        "[name='intercom-messenger-frame']",
    ) as HTMLIFrameElement;
    if (!intercomMessengerFrame) {
        LOGGER.warn('Intercom messenger frame not found, retrying');
        return;
    }
    const frameBody = intercomMessengerFrame.contentDocument.body;
    intercomMutationObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
            if (
                mutation.type === 'childList' &&
                mutation.addedNodes.length > 0 &&
                mutation.addedNodes[0].nodeType === Node.ELEMENT_NODE &&
                (mutation.addedNodes[0] as Element).querySelectorAll(
                    '[data-testid="conversation-part"]',
                )?.length &&
                mutation.addedNodes[0].textContent?.match(/⏺/g)?.length === 1
            ) {
                startFullReplay(onReplayStart, isUnmaskedReplay());
            }
        });
    });
    intercomMutationObserver.observe(frameBody, {
        childList: true,
        subtree: true,
    });
};

export const watchForFullReplay = (
    onReplayStart: (replayId: string) => void,
) => {
    if (!(window as any).Intercom) {
        LOGGER.warn('Intercom not found');
    }
    let observer: MutationObserver;
    // TODO (@Sreeni): Swap this out for a prism API poll

    (window as any).Intercom('onShow', () => {
        setTimeout(() => {
            watchMessengerFrame(onReplayStart);
        }, 1000);
    });
    (window as any).Intercom('onHide', () => {
        observer?.disconnect();
    });
};

export const initializeReplay = async (sampleRate: number, isUnmaskedReplay: boolean) => {
    try {
        const { initReplayService, startAnonymizedReplay } = await import(
            '@thoughtspot/observability'
        );
        const anonymizedDSN = getSentryAnonymizedDSN();
        if (!anonymizedDSN) {
            LOGGER.warn('Sentry DSN is empty; skipping replay initialization');
            return;
        }
        await initReplayService({
            dsn: anonymizedDSN,
            userId: getUserGuid(),
            releaseVersion: getReleaseVersion(),
            clusterId: getClusterId(),
            clusterName: getClusterName(),
            userLocale: getClientLocale(),
            orgId: getCurrentOrgId().toString(),
            isEmbedded: isAppInIFrame(),
            env: isNotAProductionCluster() ? 'dev' : 'production',
        });
        if (Math.random() < sampleRate) {
            LOGGER.info('Sampling replay');
            await startAnonymizedReplay(isUnmaskedReplay);
        }
    } catch (err) {
        LOGGER.error(err);
    }
};

let globalMeter: Meter = null;

type Records = {
    renderCounter?: any;
    firstRenderTimer?: any;
    renderTimer?: any;
};

export const useMetrics = (componentName: string) => {
    const records = useRef<Records>({});
    const time = useRef<DOMHighResTimeStamp>(performance.now());
    const isFirstRender = useRef<boolean>(true);

    useEffect(() => {
        if (!globalMeter) {
            try {
                const newMeter = getMeter(componentName);
                globalMeter = newMeter;
            } catch (e) {
                // Telemetry hasn't been initialized so no=op
                // Can't return here since we need to mark firstRender
            }
        }
        if (isFirstRender.current) {
            isFirstRender.current = false;
            if (!globalMeter) {
                // Telemetry hasn't been initialized so don't do anything
                return;
            }
            const elapsedTime = performance.now() - time.current;
            // First-renders are heavier than re-renders so count them separately
            records.current.firstRenderTimer = globalMeter.createGauge(
                `${componentName}_first_render_time`,
            );
            records.current.firstRenderTimer.record(elapsedTime);
            records.current.renderCounter = globalMeter.createCounter(
                `${componentName}_render_count`,
                { valueType: ValueType.INT },
            );
            records.current.renderCounter.add(1);
            time.current = performance.now();
            return;
        }
        if (!globalMeter) {
            // Telemetry hasn't been initialized so don't do anything
            return;
        }
        if (!records.current.renderTimer) {
            records.current.renderTimer = globalMeter.createHistogram(
                `${componentName}_render_time`,
            );
        }
        records.current.renderTimer.record(performance.now() - time.current);
        // TODO (Sreeni): Is it worth counting renders with a useState for the else to this if?
        if (records.current.renderCounter) {
            records.current.renderCounter.add(1);
        }
        time.current = performance.now();
    });
};

export const useTracing = (componentName: string) => {
    const tracer = getTracer(componentName);
    const span = tracer.startSpan(`${componentName}_render`);
    useEffect(() => {
        span.end();
    }, []);
};

export const initializeObservability = async () => {
    const observabilityConfig: OBSERVABILITY_CONFIG | any = isFeatureEnabled(
        FEATURE_FLAG_NAMES.OBSERVABILITY_CONFIG,
    );
    const observabilityPromises = [];
    if (!observabilityConfig) {
        return;
    }
    if (
        observabilityConfig?.telemetryEnabled &&
        Math.random() < (observabilityConfig?.telemetrySampleRate ?? 0)
    ) {
        initTelemetry(OTLP_CONFIG);
        LOGGER = getLogger('observability-service');
        observabilityPromises.push(
            registerInstrumentations({
                instrumentations: getWebAutoInstrumentations(),
            }),
        );
    }
    if (observabilityConfig?.replayEnabled) {
        observabilityPromises.push(
            initializeReplay(observabilityConfig?.replaySampleRate ?? 0, isUnmaskedReplay()),
        );
    }
    await Promise.all(observabilityPromises);
};

export * from '@thoughtspot/observability';
